import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {getUserCommissionDetail, getUserInfo} from './proxy.service';
import {defaultHeaderImg, whatsappIcon} from './proxy.variable';
import {
  LastActiveLabel,
  ProxyCard,
  ProxyEmpty,
  ProxyH5Header,
  ProxySectionTitle,
  getLevelImage,
  goWhatsAppChat,
  toApiDate,
  toPriceStr,
  useInfinitePager,
} from './proxy-h5.shared';

type UserInfo = {
  activeDay: string;
  agentLevel: number;
  commissionAmount: string;
  registerDate: string;
  totalUserCount: number;
  userAvatar: string;
  userId: number;
  userPhone: string;
  contactPhone: string;
  rechargeUserCount: number;
  betUserCount: number;
  rechargeAmount: string;
  betAmount: string;
  newUserCount: number;
  wonLostAmount: string;
};

type ActionItem = {
  createTime: string;
  type: number;
  amount: string;
  commissionAmount: string;
  gameName: string;
  rebate: string;
  result: string;
};

const ProxyCommissionDetailUser = (props: any) => {
  const {t} = useTranslation();
  const userId = props?.route?.params?.userId;
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [sort, setSort] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  React.useEffect(() => {
    if (!userId) {
      return;
    }
    getUserInfo(+userId).then(result => setUserInfo(result as any));
  }, [userId]);

  const request = React.useCallback(
    (pageNo: number, pageSize: number) =>
      getUserCommissionDetail({
        pageNo,
        pageSize,
        commissionSort: sort,
        startDate: startDate ? +toApiDate(startDate)! : null,
        endDate: endDate ? +toApiDate(endDate)! : null,
        userId: +userId,
      }),
    [endDate, sort, startDate, userId],
  );

  const {items, loading, loadingMore, isEnd, sentinelRef} =
    useInfinitePager<ActionItem>(request);

  return (
    <div className="proxy-h5-page flex min-h-full flex-col">
      <ProxyH5Header
        title={t('proxy.commission-detail.user.title')}
        service={false}
      />
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <div className="space-y-3">
          <ProxyCard className="p-3">
            <div className="flex items-start justify-between">
              <div className="flex">
                <div className="mr-3">
                  <img
                    src={userInfo?.userAvatar || defaultHeaderImg}
                    alt=""
                    className="h-12 w-12 rounded-full"
                  />
                  <img
                    src={getLevelImage(userInfo?.agentLevel)}
                    alt=""
                    className="mt-2 h-5 w-[3.1875rem]"
                  />
                </div>
                <div>
                  <div className="text-sm font-bold text-[var(--proxy-main)]">
                    {userInfo?.userPhone || '-'}
                  </div>
                  {userInfo?.agentLevel !== 1 && (
                    <div className="mt-1 flex items-center text-xs text-[var(--proxy-text)]">
                      <span>ID: {userInfo?.userId}</span>
                    </div>
                  )}
                  <div className="mt-1">
                    <LastActiveLabel text={userInfo?.activeDay} />
                  </div>
                  <div className="mt-2 text-xs text-[var(--proxy-text)]">
                    {t('proxy.user.register-date')}:{' '}
                    <span className="text-[var(--proxy-main)]">
                      {userInfo?.registerDate || '-'}
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="border-0 bg-transparent p-0"
                onClick={() => goWhatsAppChat(userInfo?.contactPhone)}>
                <img src={whatsappIcon} alt="whatsapp" className="h-8 w-8" />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 rounded bg-[#f8fafc] p-3 text-xs text-[var(--proxy-text)]">
              <div>
                <div>{t('proxy.user.total-user')}</div>
                <div className="mt-1 text-sm font-bold text-[var(--proxy-main)]">
                  {userInfo?.totalUserCount || 0}
                </div>
              </div>
              <div>
                <div>{t('proxy.user.commission')}</div>
                <div className="mt-1 text-sm font-bold text-[var(--proxy-main)]">
                  {toPriceStr(+(userInfo?.commissionAmount || 0))}
                </div>
              </div>
              <div>
                <div>{t('proxy.team-report.recharge-user')}</div>
                <div className="mt-1 text-sm font-bold text-[var(--proxy-main)]">
                  {userInfo?.rechargeUserCount || 0}
                </div>
              </div>
              <div>
                <div>{t('proxy.team-report.betting')}</div>
                <div className="mt-1 text-sm font-bold text-[var(--proxy-main)]">
                  {toPriceStr(+(userInfo?.betAmount || 0))}
                </div>
              </div>
            </div>
          </ProxyCard>

          <ProxyCard className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <ProxySectionTitle
                title={t('proxy.commission-detail.user.title')}
              />
              <div className="ml-auto flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={event => setStartDate(event.target.value)}
                  className="rounded border border-[var(--proxy-line)] px-2 py-1 text-xs"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={event => setEndDate(event.target.value)}
                  className="rounded border border-[var(--proxy-line)] px-2 py-1 text-xs"
                />
                <button
                  type="button"
                  className="rounded border border-[var(--proxy-line)] px-3 py-1 text-xs font-bold"
                  onClick={() => setSort(current => (current === 1 ? 0 : 1))}>
                  {sort === 1 ? 'DESC' : 'ASC'}
                </button>
              </div>
            </div>
          </ProxyCard>

          <div className="space-y-2">
            {!loading && items.length === 0 && (
              <ProxyEmpty
                title={t('proxy.user.error-no-performance')}
                actionLabel={t('proxy.user.contact')}
                onAction={() => goWhatsAppChat(userInfo?.contactPhone)}
              />
            )}
            {items.map((item, index) => (
              <ProxyCard key={`${item.createTime}-${index}`} className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-[var(--proxy-main)]">
                      {item.type === 2
                        ? t('proxy.team-report.betting')
                        : t('proxy.team-report.recharge')}
                    </div>
                    <div className="mt-1 text-xs text-[var(--proxy-text)]">
                      {item.createTime}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-[#009B22]">
                      {toPriceStr(+item.commissionAmount || 0)}
                    </div>
                    <div className="mt-1 text-xs text-[var(--proxy-text)]">
                      {toPriceStr(+item.amount || 0)}
                    </div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-[var(--proxy-text)]">
                  <div>
                    <div>Game</div>
                    <div className="mt-1 text-[var(--proxy-main)]">
                      {item.gameName || '-'}
                    </div>
                  </div>
                  <div>
                    <div>Rebate</div>
                    <div className="mt-1 text-[var(--proxy-main)]">
                      {item.rebate || '-'}
                    </div>
                  </div>
                  <div>
                    <div>Result</div>
                    <div className="mt-1 text-[var(--proxy-main)]">
                      {item.result || '-'}
                    </div>
                  </div>
                </div>
              </ProxyCard>
            ))}
            {loading && (
              <div className="py-3 text-center text-sm text-[var(--proxy-text)]">
                Loading...
              </div>
            )}
            <div ref={sentinelRef} />
            {loadingMore && (
              <div className="py-3 text-center text-sm text-[var(--proxy-text)]">
                Loading...
              </div>
            )}
            {!loading && !loadingMore && isEnd && items.length > 0 && (
              <div className="py-3 text-center text-xs text-[var(--proxy-text)]">
                {t('label.noMoreData')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProxyCommissionDetailUser;
